import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./Toolbar.scss";

import { makeStyles, Button } from "@fluentui/react-components";
import { ArrowExitFilled, DoorArrowLeft24Regular } from "@fluentui/react-icons";
import withConfig from "../config-validation/withConfig";
import ToolbarMic from "./FluentToolbarMicButton";
import ToolbarVideo from "./FluentToolbarVideoButton";
import ToolbarEmojisContainer from "./FluentToolbarEmojisContainer";
import ToolbarScreenShare from "./FluentToolbarShareScreenButton";
import ToolbarMoreButtonContainer from "./FluentToolbarMoreButtonContainer";
import { SocialButtonContainer } from "./FlexSocialButtonContainer";
import { ToolbarRaiseHandButtonContainer } from "./FluentToolbarRaiseHandButtonContainer";
import { FluentToolbarCCButton } from "./FluentToolbarCCButton";
import { usePeopleList } from "../room/usePeopleList";
import { SocialProvider } from "../contexts/SocialContext";
import { useModalContext } from "../room/ModalContext";
import { LeaveReason, LeaveRoomModal } from "../room/LeaveRoomModal";
import { FormattedMessage } from "react-intl";
import { useStateContext } from "../room/StateContext";
import { useCssBreakpoints } from "react-use-css-breakpoints";
import { FluentFileUpload } from "./FluentFileUpload";
import { isCameraShareEnabled, isScreenShareEnabled } from "../../utils/media-sharing-config-utils";
import { canAccessScreenShareAction } from "./toolbar-screen-share-availability";
import { isWebRtcDisabled } from "../../utils/room-entry-admission";

const useStyles = makeStyles({
  buttonSplit: {
    display: "flex",
    width: "72px",
    height: "40px",
    maxHeight: "40px",
    alignItems: "flex-start",
    "& button": {
      height: "100%",
      backgroundColor: "var(--toolbar-overlay-background)",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)"
      },
      "& svg": {
        color: "var(--color-text-primary)",
        fill: "var(--color-text-primary)"
      }
    },
    "&& .fui-SplitButton__primaryActionButton": {
      backgroundColor: "var(--toolbar-overlay-background)",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)"
      }
    },
    "&& .fui-SplitButton__menuButton": {
      backgroundColor: "var(--toolbar-overlay-background)",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)"
      }
    },
    "& button:first-child": {
      backgroundColor: "var(--toolbar-overlay-background)",
      width: "40px",
      minWidth: "40px",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)"
      },
      "& span": {
        width: "24px",
        height: "24px",
        fontSize: "24px"
      }
    },
    "& button:last-child": {
      backgroundColor: "var(--toolbar-overlay-background)",
      padding: "5px var(--spacingHorizontalM)",
      "& span": {
        width: "16px",
        height: "16px",
        fontSize: "16px"
      }
    }
  },
  buttonSplitDisabled: {
    "&& .fui-SplitButton__primaryActionButton": {
      backgroundColor: "var(--toolbar-overlay-background)",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)!important"
      }
    },
    "&& .fui-SplitButton__menuButton": {
      backgroundColor: "var(--toolbar-overlay-background)",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)!important"
      }
    },
    "& button:first-child": {
      backgroundColor: "var(--toolbar-overlay-background)",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)!important"
      }
    },
    "& button:last-child": {
      backgroundColor: "var(--toolbar-overlay-background)",
      "&:hover, &:active, &:hover:active": {
        backgroundColor: "var(--toolbar-overlay-background)!important"
      }
    },
    "& svg": {
      color: "var(--colorNeutralForegroundDisabled, #F0F0F0)",
      fill: "var(--colorNeutralForegroundDisabled, #F0F0F0)"
    }
  },
  button: {
    height: "40px",
    minWidth: "40px",
    backgroundColor: "var(--toolbar-overlay-background)",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "var(--color-text-primary)",
      fill: "var(--color-text-primary)"
    }
  },
  buttonDisabled: {
    backgroundColor: "var(--toolbar-overlay-background)",
    "&:hover": {
      backgroundColor: "var(--toolbar-overlay-background)"
    },
    "& svg": {
      color: "var(--colorNeutralForegroundDisabled, #F0F0F0)",
      fill: "var(--colorNeutralForegroundDisabled, #F0F0F0)"
    }
  },
  iconDisabled: {
    color: "var(--colorNeutralForegroundDisabled, #F0F0F0)!important",
    fill: "var(--colorNeutralForegroundDisabled, #F0F0F0)!important"
  },
  buttonActive: {
    backgroundColor: "var(--primaryColorBackground)",
    height: "40px",
    minWidth: "40px",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "var(--color-text-primary)",
      fill: "var(--color-text-primary)"
    }
  },
  buttonExit: {
    height: "40px",
    minWidth: "40px",
    backgroundColor: "var(--color-danger)",
    "&:hover": {
      backgroundColor: "var(--color-danger)"
    },
    "&:hover:active": {
      backgroundColor: "var(--color-danger)"
    },
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "var(--action-text-color)",
      fill: "var(--action-text-color)"
    }
  },
  buttonPressed: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  menuListItem: {
    "&:hover": {
      backgroundColor: "var(--accent1-color-hover)"
    },
    "&:hover > * ": {
      color: "inherit!important"
    }
  },
  micPopoverContainerVideo: {
    minWidth: "320px"
  },
  menuPopover: {
    top: "-15px!important"
  },
  micProgressContainer: {
    pointerEvents: "none",
    alignItems: "center",
    "&:hover": {},
    "&:hover > * ": {
      color: "inherit!important"
    }
  },
  progressBarColor: {
    "& .fui-ProgressBar__bar": {
      backgroundColor: "var(--color-success)"
    }
  }
});

export function Toolbar({
  className,
  showToolbar = true,
  store,
  scene = window.APP?.scene,
  hubChannel,
  mySessionId,
  presences,
  hub,
  signedIn,
  dialogConnected,
  hasDialogConnectedOnce,
  performConditionalSignIn,
  participantLayoutEnabled = false,
  participantLayoutForced = false,
  participantLayoutRoomEnabled,
  participantLayoutForcedState,
  onParticipantLayoutToggle,
  onParticipantLayoutRoomStateChange,
  onParticipantLayoutManagementCheckedValueChange,
  ...rest
}) {
  const classes = useStyles();
  const people = usePeopleList(presences, mySessionId);
  const modalContext = useModalContext() || {};
  const { showNonHistoriedDialog } = modalContext;

  const stateContext = useStateContext() || {};
  const { entered, watching, onWatchingChange } = stateContext;

  const breakpoint = useCssBreakpoints();

  const isMobile = breakpoint === "xs" || breakpoint === "sm" || breakpoint === "md";

  return (
    <div className={classNames(styles.toolbar, className, { hid: !showToolbar })}>
      <SocialProvider people={people} mySessionId={mySessionId}>
        {watching && (
          <Button
            appearance="subtle"
            icon={<DoorArrowLeft24Regular />}
            label={<FormattedMessage id="toolbar.join-room-button" defaultMessage="Go back" />}
            className={classes.button}
            onClick={() => onWatchingChange(false)}
          />
        )}
        {entered && (
          <>
            <Toolbar.Mic styles={classes} scene={scene} />
            {hubChannel.can("spawn_camera") && <Toolbar.Video styles={classes} scene={scene} hubChannel={hubChannel} />}
            {canAccessScreenShareAction({
              canSpawnAndMoveMedia: hubChannel.can("spawn_and_move_media")
            }) && <Toolbar.ShareScreen styles={classes} scene={scene} hubChannel={hubChannel} />}
            {hubChannel.can("spawn_emoji") && (
              <Toolbar.EmojisContainer
                styles={classes}
                scene={scene}
                hubChannel={hubChannel}
                participantLayoutReactionTargetId={mySessionId}
              />
            )}
            {!isMobile && hubChannel.can("spawn_emoji") && (
              <Toolbar.RaiseHandButtonContainer
                hubChannel={hubChannel}
                initialPresence={presences && presences[mySessionId]}
                store={store}
                styles={classes}
                scene={scene}
              />
            )}
            {!isMobile && <FluentToolbarCCButton styles={classes} />}

            {/* TODO: Move this to the root file */}
            <FluentFileUpload performConditionalSignIn={performConditionalSignIn} store={store} />
          </>
        )}
        <Toolbar.SocialButtonContainer
          styles={classes}
          scene={scene}
          hubChannel={hubChannel}
          mySessionId={mySessionId}
          people={people}
          presences={presences}
          entered={entered}
          dialogConnected={dialogConnected}
          hasDialogConnectedOnce={hasDialogConnectedOnce}
          participantLayoutRoomEnabled={participantLayoutRoomEnabled}
          participantLayoutForcedState={participantLayoutForcedState}
          onParticipantLayoutRoomStateChange={onParticipantLayoutRoomStateChange}
        />
        <Toolbar.MoreButton
          styles={classes}
          store={store}
          scene={scene}
          hubChannel={hubChannel}
          hub={hub}
          signedIn={signedIn}
          people={people}
          initialPresence={presences && presences[mySessionId]}
          participantLayoutEnabled={participantLayoutEnabled}
          participantLayoutForced={participantLayoutForced}
          participantLayoutRoomEnabled={participantLayoutRoomEnabled}
          participantLayoutForcedState={participantLayoutForcedState}
          onParticipantLayoutToggle={onParticipantLayoutToggle}
          onParticipantLayoutManagementCheckedValueChange={onParticipantLayoutManagementCheckedValueChange}
          performConditionalSignIn={performConditionalSignIn}
          {...rest}
        />
        <Button
          appearance="subtle"
          className={classes.buttonExit}
          icon={<ArrowExitFilled style={{ width: "24px", height: "24px" }} />}
          onClick={() =>
            showNonHistoriedDialog(LeaveRoomModal, {
              reason: LeaveReason.leaveRoom,
              onLeave: () => {
                scene.emit("leave_room_requested");
              }
            })
          }
        />
      </SocialProvider>
    </div>
  );
}

Toolbar.propTypes = {
  className: PropTypes.string,
  left: PropTypes.node,
  center: PropTypes.node,
  right: PropTypes.node,
  hideLeft: PropTypes.string,
  hideRight: PropTypes.string,
  showToolbar: PropTypes.bool,
  scene: PropTypes.object,
  hubChannel: PropTypes.object,
  store: PropTypes.object,
  mySessionId: PropTypes.string,
  presences: PropTypes.object,
  hub: PropTypes.object,
  signedIn: PropTypes.bool,
  dialogConnected: PropTypes.bool,
  hasDialogConnectedOnce: PropTypes.bool,
  objectFocused: PropTypes.bool,
  performConditionalSignIn: PropTypes.func,
  participantLayoutEnabled: PropTypes.bool,
  participantLayoutForced: PropTypes.bool,
  participantLayoutRoomEnabled: PropTypes.bool,
  participantLayoutForcedState: PropTypes.string,
  onParticipantLayoutToggle: PropTypes.func,
  onParticipantLayoutRoomStateChange: PropTypes.func,
  onParticipantLayoutManagementCheckedValueChange: PropTypes.func
};

Toolbar.Mic = withConfig(ToolbarMic, config => !isWebRtcDisabled() && config?.voiceChat?.enabled);

Toolbar.Video = withConfig(ToolbarVideo, config => !isWebRtcDisabled() && isCameraShareEnabled(config));

Toolbar.ShareScreen = withConfig(ToolbarScreenShare, config => !isWebRtcDisabled() && isScreenShareEnabled(config));

Toolbar.EmojisContainer = withConfig(ToolbarEmojisContainer, `spawnEmoji.enabled`);

Toolbar.RaiseHandButtonContainer = withConfig(ToolbarRaiseHandButtonContainer, `spawnEmoji.enabled`);

Toolbar.MoreButton = withConfig(ToolbarMoreButtonContainer, `toolbar.moreButton.enabled`);

Toolbar.SocialButtonContainer = SocialButtonContainer;
Toolbar.CCButton = FluentToolbarCCButton;
